import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import DataTable from '../../components/ui/DataTable';
import Badge from '../../components/ui/Badge';
import Modal, { ConfirmModal } from '../../components/ui/Modal';
import Input, { Select, Textarea, Checkbox } from '../../components/ui/Input';
import Spinner from '../../components/ui/Spinner';
import { adminApi } from '../../config/axios';
import { useGlobal } from '../../contexts/GlobalContext';

/**
 * Hardware Setup tab for a company in the Admin Panel.
 *
 * Acts as the company via the /admin/companies/{companyId}/* routes added in
 * routes/admin.php. The AdminActsAsCompany middleware swaps the auth user's
 * company_id so every existing tenant-side controller (HardwareController,
 * ReceiverController, ScreenController, SceneController, ChannelController)
 * scopes reads + writes to the target tenant — no controller forks needed.
 *
 * Four sub-tabs:
 *   • Devices    — Control Box / Encoder / Decoder / iTach CRUD
 *   • Receivers  — wires Player → Encoder → iTach; nested Channels manager
 *   • Screens    — physical screens bound to a decoder
 *   • Scenes     — multi-action presets (kept compact; full action builder is
 *                  in the Dashboard's ScenesPage for now)
 *
 * One-file module so the existing Companies/Profile.jsx stays small —
 * imports `<CompanyHardware companyId={id} />` as a fifth tab.
 */

// Non-blocking add/edit-time warnings (HardwareController::create/update's
// `warnings` array, e.g. HardwareService::checkSubnetPlausibility()).
// HardwareController wraps its payload in CompanyResource ({data: {...}}),
// and axios's own response.data unwraps one layer on top of that — so the
// real array sits two levels down. Exported and unit-tested on its own
// (no @testing-library/react in this repo — see hardwareWarnings.test.js)
// so a wrapping-shape change fails here directly instead of only ever
// showing up as "the warning banner silently never renders".
export const extractHardwareWarnings = (response) => response?.data?.data?.warnings ?? [];

export default function CompanyHardware({ companyId }) {
  const SECTIONS = [
    { key: 'devices',   label: 'Devices',   icon: '🖥️' },
    { key: 'receivers', label: 'Receivers', icon: '📡' },
    { key: 'screens',   label: 'Screens',   icon: '🖼️' },
    { key: 'scenes',    label: 'Scenes',    icon: '🎬' },
  ];
  const [section, setSection] = useState('devices');

  return (
    <div className="space-y-4">
      {/* Sub-tab pills */}
      <div className="flex gap-1 bg-bg-card border border-white/[0.08] rounded-lg p-1 w-fit">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setSection(s.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              section === s.key ? 'bg-accent text-white' : 'text-text-muted hover:text-text-main'
            }`}
          >
            <span className="me-1">{s.icon}</span>{s.label}
          </button>
        ))}
      </div>

      {section === 'devices'   && <DevicesSection   companyId={companyId} />}
      {section === 'receivers' && <ReceiversSection companyId={companyId} />}
      {section === 'screens'   && <ScreensSection   companyId={companyId} />}
      {section === 'scenes'    && <ScenesSection    companyId={companyId} />}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Shared hook: paginated client-side table over a non-paginated admin
 * endpoint that returns { data: [...] }. The existing useServerTable
 * expects { data, total } per page, which doesn't fit our flat lists.
 * ──────────────────────────────────────────────────────────────────── */
function useAdminList(url, deps = []) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.get(url)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data ?? res.data ?? [];
        setRows(Array.isArray(payload) ? payload : []);
      })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, version, ...deps]);

  // Render rows directly (no pagination) — these lists are small (max few dozen).
  return { rows, loading, refresh, total: rows.length };
}

/* ──────────────────────────────────────────────────────────────────────
 * Form-field helper — mirrors the design-system FormField from the Dashboard
 * so admin forms look modern + consistent. Pure Tailwind, no PrimeReact.
 * ──────────────────────────────────────────────────────────────────── */
function Field({ icon, label, required, hint, error, children }) {
  return (
    <div className="space-y-1.5">
      <label className="label flex items-center gap-1.5">
        {icon && <span className="text-accent">{icon}</span>}
        <span>{label}</span>
        {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {error
        ? <p className="text-xs text-danger flex items-center gap-1">⚠ {error}</p>
        : hint
        ? <p className="text-xs text-text-muted">{hint}</p>
        : null}
    </div>
  );
}

function Empty({ icon = '📭', title, description, ctaLabel, onCta }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-2">
      <div className="text-4xl mb-1">{icon}</div>
      <p className="text-base font-semibold text-text-main">{title}</p>
      {description && <p className="text-sm text-text-muted max-w-md">{description}</p>}
      {ctaLabel && (
        <button onClick={onCta} className="btn-primary mt-2">{ctaLabel}</button>
      )}
    </div>
  );
}

function Section({ title, count, addLabel, onAdd, children }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-text-main">{title}</h3>
          <Badge variant="muted">{count}</Badge>
        </div>
        <button onClick={onAdd} className="btn-primary text-sm">+ {addLabel}</button>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Section: DEVICES — Control Box / Encoder / Decoder / iTach
 * ──────────────────────────────────────────────────────────────────── */
// Role labels name the ROLE, not a vendor — "PTN Control Box" etc. was wrong
// the moment a second matrix vendor (SC&T, channel-routed, no control box at
// all) existed. Which vendor a device speaks is the Driver dropdown below,
// sourced live from the backend, never this list.
const ROLE_OPTIONS = [
  { value: 'control_box', label: 'Control Box' },
  { value: 'encoder',     label: 'Encoder (port)' },
  { value: 'decoder',     label: 'Decoder (port)' },
  { value: 'ir_blaster',  label: 'iTach IR Blaster' },
];
const ROLE_TO_TYPE = { control_box: 'matrix', encoder: 'matrix', decoder: 'matrix', ir_blaster: 'itach' };
// PTN IP5000 control unit speaks its TCP command protocol on 4001 (NOT telnet 23).
const DEFAULT_PORT  = { control_box: 4001, ir_blaster: 4998, encoder: null, decoder: null };
const ROLE_LABEL    = (r) => ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r;

// Driver options are NOT hardcoded here — that array used to claim to
// "mirror backend DriverFactory::driversForType()" and silently drifted out
// of sync (missing sct_ast1500_telnet entirely for months). Both the matrix
// and iTach driver lists are now fetched live from GET /hardware/driver-types
// in DevicesSection and passed down as props — a driver registered on the
// backend appears here with zero frontend change.

function DevicesSection({ companyId }) {
  const { showToast } = useGlobal();
  const { rows, loading, refresh } = useAdminList(`/companies/${companyId}/hardware`);
  const { rows: controlBoxes } = useAdminList(`/companies/${companyId}/hardware?role=control_box`);
  // Players for the Bridge Player dropdown (the on-LAN Android device that
  // relays commands/health probes to a control box).
  const { rows: players } = useAdminList(`/companies/${companyId}/devices/all`);
  // Driver lists, live from the backend — each entry: { value, label,
  // requires_control_box }. requires_control_box is null for iTach drivers
  // (the concept only applies to matrix drivers); it drives whether the
  // Parent Control Box field is required/shown, and whether the channel/
  // login fields for a channel-routed vendor appear.
  const { rows: matrixDrivers } = useAdminList(`/companies/${companyId}/hardware/driver-types?type=matrix`);
  const { rows: irDrivers }     = useAdminList(`/companies/${companyId}/hardware/driver-types?type=itach`);

  const [open, setOpen]   = useState(false);
  const [edit, setEdit]   = useState(null);
  const [del,  setDel]    = useState(null);
  const [testing, setTesting] = useState(null); // id being probed

  const startCreate = () => { setEdit(null); setOpen(true); };
  const startEdit   = (row) => { setEdit(row); setOpen(true); };

  // Test Connection — relays a read-only firmware probe through the bridge
  // player (control boxes can't be reached from the cloud directly). Async:
  // the status flips when the player POSTs the result back, so we refresh.
  const onTest = async (row) => {
    setTesting(row.id);
    try {
      const { data } = await adminApi.get(`/companies/${companyId}/hardware/${row.id}/status`);
      const r = data?.data ?? data ?? {};
      if (r.pending) {
        showToast('info', 'Testing', 'Probe sent via bridge player — refreshing…');
        setTimeout(refresh, 2000); setTimeout(refresh, 5000);
      } else if (r.success) {
        const detail = r.firmware ? `Reachable — ${r.firmware}` : (r.name ? `Online — ${r.name}` : 'Reachable.');
        showToast('success', 'Online', detail);
        refresh();
      } else {
        showToast('error', 'Unreachable', r.error ?? 'No response.');
        refresh();
      }
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Probe failed.');
    } finally {
      setTesting(null);
    }
  };

  const onDelete = async () => {
    if (!del) return;
    try {
      await adminApi.delete(`/companies/${companyId}/hardware/${del.id}/delete`);
      showToast('success', 'Deleted', `${del.name} removed.`);
      setDel(null); refresh();
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.');
    }
  };

  const cols = [
    { key: 'name', header: 'Name', render: (r) => <strong>{r.name}</strong> },
    { key: 'device_role', header: 'Role', render: (r) => <Badge variant="accent">{ROLE_LABEL(r.device_role)}</Badge> },
    { key: 'ip_address', header: 'IP', render: (r) => r.ip_address
      ? <code className="text-xs">{r.ip_address}{r.port ? `:${r.port}` : ''}</code>
      : <span className="text-text-muted">—</span> },
    { key: 'port_number', header: 'Port #', render: (r) => r.port_number ?? <span className="text-text-muted">—</span> },
    { key: 'parent_device', header: 'Control Box',
      render: (r) => r.parent_device?.name ?? <span className="text-text-muted">—</span> },
    { key: 'status', header: 'Status',
      render: (r) => <Badge variant={r.status === 'online' ? 'teal' : r.status === 'offline' ? 'danger' : 'muted'} dot>
        {r.status ?? 'unknown'}
      </Badge> },
    // player_gate (backend: VenueHardware::getPlayerGateAttribute) — non-null
    // whenever this device's driver needs a Firebase verb (e.g. matrix_switch
    // for a Telnet vendor) not every deployed player understands yet. Was
    // computed backend-side but shown nowhere until now — a device that
    // can't actually route looked identical to one that could.
    { key: 'player_gate', header: 'Player',
      render: (r) => r.player_gate
        ? <Badge variant="danger" title={r.player_gate.message}>⚠ {r.player_gate.message}</Badge>
        : <span className="text-text-muted">—</span> },
  ];

  return (
    <Section title="Hardware Devices" count={rows.length} addLabel="Add Device" onAdd={startCreate}>
      {loading ? (
        <div className="p-6 flex justify-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <Empty icon="🖥️"
          title="No hardware devices yet"
          description="Add a Control Box first (or a parentless Encoder/Decoder for a channel-routed vendor like SC&T), then any iTach IR blasters."
          ctaLabel="+ Add Device" onCta={startCreate} />
      ) : (
        <DataTable
          columns={cols} data={rows} total={rows.length} page={0} perPage={rows.length}
          onPageChange={() => {}} onPerPageChange={() => {}}
          actions={(row) => (
            <div className="flex gap-1">
              {(row.device_role === 'control_box' || row.device_role === 'ir_blaster') && (
                <button className="btn-icon" title="Test Connection"
                  disabled={testing === row.id}
                  onClick={() => onTest(row)}>{testing === row.id ? '…' : '⚡'}</button>
              )}
              <button className="btn-icon" title="Edit" onClick={() => startEdit(row)}>✎</button>
              <button className="btn-icon text-danger" title="Delete" onClick={() => setDel(row)}>🗑</button>
            </div>
          )}
        />
      )}

      <DeviceFormModal
        open={open} onClose={() => setOpen(false)}
        companyId={companyId} editing={edit} controlBoxes={controlBoxes} players={players}
        matrixDrivers={matrixDrivers} irDrivers={irDrivers}
        refresh={refresh}
        onSaved={() => { setOpen(false); refresh(); }}
      />
      <ConfirmModal
        open={!!del} onClose={() => setDel(null)} onConfirm={onDelete}
        title="Delete device" message={`Permanently remove "${del?.name}"? This cannot be undone.`}
      />
    </Section>
  );
}

export function DeviceFormModal({ open, onClose, companyId, editing, controlBoxes, players, matrixDrivers, irDrivers, refresh, onSaved }) {
  const { showToast } = useGlobal();
  const { register, handleSubmit, watch, reset, setValue, formState: { errors, isSubmitting } } =
    useForm({ defaultValues: {
      role: 'control_box', port: 4001, port_number: 1, driver_type: '',
      bridge_serial: '', tuya_device_id: '', tuya_remote_id: '',
      channel: '', login_username: '', login_password: '',
    } });
  // Non-blocking add/edit-time warnings (HardwareService::checkSubnetPlausibility
  // on the backend, e.g. "this encoder is on a different subnet than its
  // siblings"). Cleared below whenever the modal opens/re-opens so a stale
  // warning from a previous save never lingers into the next one.
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    setWarnings([]);
    if (!open) return;
    if (editing) {
      const role = editing.device_role ?? (editing.type === 'itach' ? 'ir_blaster' : 'control_box');
      reset({
        role,
        name: editing.name ?? '',
        ip_address: editing.ip_address ?? '',
        port: editing.port ?? DEFAULT_PORT[role] ?? 4001,
        port_number: editing.port_number ?? 1,
        parent_device_id: editing.parent_device_id ?? '',
        driver_type: editing.driver_type ?? '',
        bridge_serial: editing.config?.bridge_serial ?? '',
        tuya_device_id: editing.config?.tuya_device_id ?? '',
        tuya_remote_id: editing.config?.tuya_remote_id ?? '',
        // Channel-routed vendor (SC&T): channel lives on the encoder, login
        // on the decoder — see HardwareService::routeSourceByChannel() and
        // SctTelnetDriver::loginFor() on the backend.
        channel: editing.config?.channel ?? '',
        login_username: editing.config?.login?.username ?? '',
        login_password: editing.config?.login?.password ?? '',
        // «مصدر خارجي»: this encoder's input is the customer's own device, not our player's HDMI-in.
        is_external: editing.source_kind === 'external',
        source_label: editing.source_label ?? '',
      });
    } else {
      reset({ role: 'control_box', name: '', ip_address: '', port: 4001, port_number: 1,
              parent_device_id: '', driver_type: '', bridge_serial: '', tuya_device_id: '', tuya_remote_id: '',
              channel: '', login_username: '', login_password: '', is_external: false, source_label: '' });
    }
  }, [open, editing, reset]);

  const role  = watch('role');
  const driverType = watch('driver_type');
  // A Tuya IR blaster is cloud-controlled → no LAN IP / port / bridge needed;
  // instead it carries a Tuya device_id + remote_id in config.
  const isTuya = role === 'ir_blaster' && driverType === 'tuya_ir_blaster';
  const isIP  = (role === 'control_box' || role === 'ir_blaster') && !isTuya;
  const isPort = role === 'encoder' || role === 'decoder';

  // What the SELECTED driver needs, not what the role assumes — the fields
  // follow the driver. Blank ("Auto") has no entry in matrixDrivers, so it
  // defaults to requiring a control box: EXACTLY today's behaviour for every
  // existing PTN/Kramer encoder/decoder (driver_type was never set on them —
  // this preserves that unchanged, byte-for-byte).
  const selectedMatrixDriver = matrixDrivers.find((d) => d.value === driverType);
  const requiresControlBox = selectedMatrixDriver ? selectedMatrixDriver.requires_control_box : true;
  // A channel-routed encoder/decoder (SC&T today) has no control box at all
  // — it must have an explicit driver_type to even know that about itself.
  const isChannelRouted = isPort && !!driverType && requiresControlBox === false;

  // Adjust port default when role changes.
  useEffect(() => {
    if (!editing && DEFAULT_PORT[role] !== null) setValue('port', DEFAULT_PORT[role]);
  }, [role, editing, setValue]);

  // driver_type means something different per role (control-box protocol vs
  // IR driver vs port-level vendor override) — clear it on a role change so
  // a value picked under one role never leaks into another via react-hook-
  // form's persisted field state. Same !editing guard as the port effect
  // above: role is disabled while editing, so this never fires mid-edit.
  useEffect(() => {
    if (!editing) setValue('driver_type', '');
  }, [role, editing, setValue]);

  const onSubmit = async (data) => {
    // Use the watched `role` (not data.role): the Role <Select> is disabled on
    // edit, and a disabled field can be dropped from react-hook-form's submit
    // data — which previously sent device_role/type empty → "type required".
    const cfg = {};
    if (role === 'control_box' && data.bridge_serial) cfg.bridge_serial = data.bridge_serial;
    // Tuya cloud IR blaster: which physical blaster + which learned remote.
    if (isTuya) {
      if (data.tuya_device_id?.trim()) cfg.tuya_device_id = data.tuya_device_id.trim();
      if (data.tuya_remote_id?.trim()) cfg.tuya_remote_id = data.tuya_remote_id.trim();
    }
    // Channel-routed vendor: the ENCODER carries which channel it
    // broadcasts; the DECODER carries the login it subscribes with.
    if (isChannelRouted && role === 'encoder' && data.channel?.trim()) {
      cfg.channel = data.channel.trim();
    }
    if (isChannelRouted && role === 'decoder') {
      const login = {};
      if (data.login_username?.trim()) login.username = data.login_username.trim();
      if (data.login_password?.trim()) login.password = data.login_password.trim();
      if (Object.keys(login).length) cfg.login = login;
      // No control box to inherit a bridge from — a channel-routed decoder
      // must carry its OWN bridge_serial, or HardwareService::
      // resolveBridgePlayer() finds nothing and the backend tries to reach
      // the venue LAN directly from Cloud Run (fails outright).
      if (data.bridge_serial) cfg.bridge_serial = data.bridge_serial;
    }
    const payload = {
      name:             data.name,
      type:             ROLE_TO_TYPE[role],
      device_role:      role,
      // Meaningful for a control box, an IR blaster, AND now an encoder/
      // decoder — a channel-routed vendor has no control box to inherit a
      // driver from, so it must be set directly on the port itself.
      driver_type:      data.driver_type || null,
      protocol:         role === 'ir_blaster' ? 'ir' : 'tcp',
      // Encoder/decoder IP is the video-VLAN address a pair-routed matrix
      // (PTN IP5000) routes by, OR a channel-routed decoder's own Telnet
      // endpoint. Optional for pair-routed — Kramer-style matrices route by
      // port_number instead. A Tuya cloud blaster has no LAN address at all.
      ip_address:       isTuya ? null : (data.ip_address || null),
      port:             (isPort || isTuya) ? null : (Number(data.port) || DEFAULT_PORT[role] || null),
      port_number:      isPort ? (Number(data.port_number) || 1) : null,
      // Required only for a driver that actually has a control box —
      // conditional on the DRIVER, not hardcoded on the role.
      parent_device_id: (isPort && requiresControlBox) ? (data.parent_device_id || null) : null,
      config:           Object.keys(cfg).length ? cfg : null,
      // «مصدر خارجي» (encoders only): shown in the Remote under «مصادر خارجية». Cleared (null) whenever the box is off.
      source_kind:      role === 'encoder' && data.is_external ? 'external' : null,
      source_label:     role === 'encoder' && data.is_external ? (data.source_label?.trim() || null) : null,
    };
    try {
      let response;
      if (editing) {
        response = await adminApi.put(`/companies/${companyId}/hardware/${editing.id}/update`, payload);
        showToast('success', 'Saved', `${data.name} updated.`);
      } else {
        response = await adminApi.post(`/companies/${companyId}/hardware/create`, payload);
        showToast('success', 'Created', `${data.name} added.`);
      }
      // HardwareController::create/update wraps its payload in CompanyResource
      // ({data: {...}}), and axios's own response.data unwraps one layer on
      // top of that — so the controller's `warnings` array lives two levels
      // down. Never blocking: the device is already saved either way.
      const rowWarnings = extractHardwareWarnings(response);
      setWarnings(rowWarnings);
      if (rowWarnings.length === 0) {
        onSaved();
      } else {
        // Keep the modal open so the admin sees the warning instead of it
        // flashing past in a toast — but the row IS saved, so refresh the
        // table behind the modal now rather than waiting for a manual close.
        refresh();
      }
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed to save device.');
    }
  };

  return (
    <Modal open={open} onClose={onClose}
      title={editing ? '✎ Edit Device' : '🖥️ Add Device'}
      footer={
        warnings.length > 0 ? (
          // Saved already (the warning is non-blocking) — only Close is
          // offered here so a re-click of Create/Save can't fire a duplicate.
          <button className="btn-primary" onClick={onClose}>Close</button>
        ) : (
          <>
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
              {editing ? 'Save' : 'Create'}
            </button>
          </>
        )
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {warnings.length > 0 && (
          <div className="flex gap-2 items-start rounded-md border border-gold/30 bg-gold/10 px-3 py-2">
            <svg className="w-4 h-4 text-gold shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="text-xs text-text-main space-y-1">
              {warnings.map((w, i) => <p key={i}>{w}</p>)}
            </div>
          </div>
        )}

        <Field icon="📋" label="Device Role" required
               hint={editing ? 'Device role cannot be changed after creation.' : 'Picks the physical role — changes the rest of the form.'}>
          <Select {...register('role', { required: true })} disabled={!!editing}>
            {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>

        <Field icon="🏷️" label="Name" required error={errors.name?.message}>
          <Input placeholder="e.g. Main Matrix"
                 {...register('name', { required: 'Required' })} />
        </Field>

        {isIP && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field icon="🌐" label="IP Address" required error={errors.ip_address?.message}>
                <Input placeholder="e.g. 192.168.0.178"
                       {...register('ip_address', { required: isIP ? 'Required' : false })} />
              </Field>
            </div>
            <Field icon="🔢" label="Port" required>
              <Input type="number" min={1} max={65535} {...register('port', { valueAsNumber: true })} />
            </Field>
          </div>
        )}

        {role === 'control_box' && (
          <>
            <Field icon="⚙️" label="Driver"
                   hint="Which matrix protocol this control box speaks. Leave on Auto for the type default. Only pair-routed vendors are offered here — a channel-routed vendor (SC&T) has no control box, set it on the Encoder/Decoder ports directly.">
              <Select {...register('driver_type')}>
                <option value="">Auto (default for type)</option>
                {matrixDrivers.filter((o) => o.requires_control_box).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </Field>
            <Field icon="📱" label="Bridge Player"
                   hint="The on-site Android player that relays commands + health checks to this control box. Required for cloud control — the backend can't reach 192.168.x directly.">
              {players.length === 0 ? (
                <p className="text-xs text-danger">No players paired for this company yet.</p>
              ) : (
                <Select {...register('bridge_serial')}>
                  <option value="">None (direct / on-LAN only)</option>
                  {players.map((p) => (
                    <option key={p.id} value={p.serial_name}>
                      {p.name ? `${p.name} (${p.serial_name})` : p.serial_name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </>
        )}

        {role === 'ir_blaster' && (
          <>
            <Field icon="⚙️" label="Driver"
                   hint="Global Caché iTach = on-LAN TCP blaster (needs a bridge player). Tuya Cloud = controlled via Tuya's cloud API, so it works straight from the cloud — no bridge, no LAN IP.">
              <Select {...register('driver_type')}>
                <option value="">Auto (Global Caché iTach)</option>
                {irDrivers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Field>

            {isTuya && (
              <>
                <Field icon="📡" label="Tuya Device ID" required error={errors.tuya_device_id?.message}
                       hint="The IR blaster's device id in the Tuya IoT platform (Devices → the blaster → Device ID).">
                  <Input placeholder="e.g. bfa44d4258b86d58acnr5m"
                         {...register('tuya_device_id', { required: isTuya ? 'Required' : false })} />
                </Field>
                <Field icon="🎛️" label="Default Remote ID" required error={errors.tuya_remote_id?.message}
                       hint="The learned remote to send channel keys to (e.g. the set-top-box remote). From the Tuya IR remotes list.">
                  <Input placeholder="e.g. bf6a276152b5717581qoqx"
                         {...register('tuya_remote_id', { required: isTuya ? 'Required' : false })} />
                </Field>
                <p className="text-xs text-text-muted">
                  ☁️ Cloud IR — no IP address or bridge player needed. Tuya account credentials are shared
                  server-side (Secret Manager); you only pick which blaster and remote.
                </p>
              </>
            )}
          </>
        )}

        {isPort && (
          <>
            <Field icon="⚙️" label="Driver"
                   hint="A pair-routed vendor (PTN, Kramer) can stay on Auto and inherit its Parent Control Box's protocol. A channel-routed vendor (SC&T) has no control box — it MUST be set here directly.">
              <Select {...register('driver_type')}>
                <option value="">Auto (inherits from Parent Control Box)</option>
                {matrixDrivers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </Select>
            </Field>

            <Field icon="🌐" label="IP Address" required={isChannelRouted} error={errors.ip_address?.message}
                   hint={isChannelRouted
                     ? "This device's own address (e.g. 192.168.40.162) — a channel-routed vendor is addressed directly, not through a control box."
                     : "The video-VLAN address a pair-routed matrix (PTN IP5000) routes by. Leave blank for Kramer-style matrices that route by port number."}>
              <Input placeholder="e.g. 192.168.90.150"
                     {...register('ip_address', { required: isChannelRouted ? 'Required for a channel-routed vendor' : false })} />
            </Field>

            <Field icon="🔢" label="Port Number" required
                   hint="Used by port-routed matrices (Kramer). Ignored by IP-routed and channel-routed vendors.">
              <Input type="number" min={1} max={64}
                     placeholder="1, 2, 3 …"
                     {...register('port_number', { valueAsNumber: true })} />
            </Field>

            {requiresControlBox ? (
              <Field icon="🖥️" label="Parent Control Box" required
                     hint="The Control Box that physically hosts this port.">
                {controlBoxes.length === 0 ? (
                  <p className="text-xs text-danger">No Control Box added yet. Add one first.</p>
                ) : (
                  <Select {...register('parent_device_id', { required: 'Required' })}>
                    <option value="">Select…</option>
                    {controlBoxes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                )}
              </Field>
            ) : (
              <p className="text-xs text-text-muted">
                No Parent Control Box for this driver — channel-routed vendors are addressed directly, not through a control box.
              </p>
            )}

            {role === 'encoder' && (
              <>
                <Checkbox id="is_external" label="External source (مصدر خارجي) - the customer's own device, not our player" {...register('is_external')} />
                {watch('is_external') && (
                  <Field icon="🏷️" label="Source label" hint="The name staff see in the Remote under «مصادر خارجية», e.g. جهاز الصالة.">
                    <Input placeholder="e.g. Apple TV" maxLength={80} {...register('source_label')} />
                  </Field>
                )}
              </>
            )}

            {isChannelRouted && role === 'encoder' && (
              <Field icon="📡" label="Channel" required error={errors.channel?.message}
                     hint="The 4-digit channel this encoder broadcasts on (e.g. 0001). Decoders subscribe to it by channel number, not by an IP pair.">
                <Input placeholder="e.g. 0001"
                       {...register('channel', { required: 'Required for a channel-routed encoder' })} />
              </Field>
            )}

            {isChannelRouted && role === 'decoder' && (
              <>
                <Field icon="🔑" label="Login Username" hint="Leave blank to use the driver's default (root).">
                  <Input placeholder="root" {...register('login_username')} />
                </Field>
                <Field icon="🔒" label="Login Password" hint="Leave blank if this device has no password set.">
                  <Input type="password" {...register('login_password')} />
                </Field>
                <Field icon="📱" label="Bridge Player"
                       hint="Required to actually route through this decoder. No control box to inherit one from — the on-site Android player that relays commands to this decoder must be set here directly.">
                  {players.length === 0 ? (
                    <p className="text-xs text-danger">No players paired for this company yet.</p>
                  ) : (
                    <Select {...register('bridge_serial')}>
                      <option value="">None — routing will fail without one</option>
                      {players.map((p) => (
                        <option key={p.id} value={p.serial_name}>
                          {p.name ? `${p.name} (${p.serial_name})` : p.serial_name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </>
            )}
          </>
        )}
      </form>
    </Modal>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Section: RECEIVERS — Player ↔ Encoder ↔ iTach + nested Channels
 * ──────────────────────────────────────────────────────────────────── */
const RECEIVER_TYPES = [
  { value: 'satellite', label: 'Satellite' },
  { value: 'iptv',      label: 'IPTV' },
  { value: 'cable',     label: 'Cable' },
];

function ReceiversSection({ companyId }) {
  const { showToast } = useGlobal();
  const { rows, loading, refresh } = useAdminList(`/companies/${companyId}/receivers`);
  const { rows: encoders } = useAdminList(`/companies/${companyId}/hardware?role=encoder`);
  const { rows: itachs   } = useAdminList(`/companies/${companyId}/hardware?role=ir_blaster`);
  const { rows: players  } = useAdminList(`/companies/${companyId}/devices/all`);

  const [open, setOpen]   = useState(false);
  const [edit, setEdit]   = useState(null);
  const [del,  setDel]    = useState(null);
  const [chanFor, setChanFor] = useState(null);

  const takenPlayers  = new Set(rows.filter((r) => r.id !== edit?.id && r.connected_player_id).map((r) => r.connected_player_id));
  const takenEncoders = new Set(rows.filter((r) => r.id !== edit?.id && r.connected_encoder_id).map((r) => r.connected_encoder_id));

  const onDelete = async () => {
    if (!del) return;
    try {
      await adminApi.delete(`/companies/${companyId}/receivers/${del.id}/delete`);
      showToast('success', 'Deleted', `${del.name} removed.`);
      setDel(null); refresh();
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.');
    }
  };

  const cols = [
    { key: 'name', header: 'Name', render: (r) => <strong>📡 {r.name}</strong> },
    { key: 'type', header: 'Type', render: (r) => <span className="capitalize">{r.type}</span> },
    { key: 'player', header: 'Player', render: (r) => r.player?.serial_name ?? <span className="text-text-muted">—</span> },
    { key: 'encoder', header: 'Encoder', render: (r) => r.encoder?.name ?? <span className="text-text-muted">—</span> },
    { key: 'itach', header: 'iTach', render: (r) => r.itach?.name ?? <span className="text-text-muted">—</span> },
    { key: 'channels', header: 'Channels',
      render: (r) => <button className="btn-secondary text-xs" onClick={() => setChanFor(r)}>Manage…</button> },
  ];

  return (
    <Section title="Receivers" count={rows.length} addLabel="Add Receiver" onAdd={() => { setEdit(null); setOpen(true); }}>
      {loading ? (
        <div className="p-6 flex justify-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <Empty icon="📡" title="No receivers yet"
          description="Wire an Android Player to a PTN encoder + iTach so the Remote Control can switch channels."
          ctaLabel="+ Add Receiver" onCta={() => { setEdit(null); setOpen(true); }} />
      ) : (
        <DataTable
          columns={cols} data={rows} total={rows.length} page={0} perPage={rows.length}
          onPageChange={() => {}} onPerPageChange={() => {}}
          actions={(row) => (
            <div className="flex gap-1">
              <button className="btn-icon" title="Edit" onClick={() => { setEdit(row); setOpen(true); }}>✎</button>
              <button className="btn-icon text-danger" title="Delete" onClick={() => setDel(row)}>🗑</button>
            </div>
          )}
        />
      )}

      <ReceiverFormModal
        open={open} onClose={() => setOpen(false)}
        companyId={companyId} editing={edit}
        players={players} encoders={encoders} itachs={itachs}
        takenPlayers={takenPlayers} takenEncoders={takenEncoders}
        onSaved={() => { setOpen(false); refresh(); }}
      />
      <ConfirmModal
        open={!!del} onClose={() => setDel(null)} onConfirm={onDelete}
        title="Delete receiver" message={`Permanently remove "${del?.name}"? Its channels will be deleted too.`}
      />
      {chanFor && (
        <ChannelsModal companyId={companyId} receiver={chanFor} onClose={() => setChanFor(null)} />
      )}
    </Section>
  );
}

function ReceiverFormModal({ open, onClose, companyId, editing, players, encoders, itachs, takenPlayers, takenEncoders, onSaved }) {
  const { showToast } = useGlobal();
  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (!open) return;
    reset({
      name: editing?.name ?? '',
      type: editing?.type ?? 'satellite',
      connected_player_id:  editing?.connected_player_id ?? '',
      connected_encoder_id: editing?.connected_encoder_id ?? '',
      itach_device_id:      editing?.itach_device_id ?? '',
      tuya_remote_id:       editing?.config?.tuya_remote_id ?? '',
    });
  }, [open, editing, reset]);

  // One IR blaster can front several receivers, each a DIFFERENT Tuya remote.
  // Show the per-receiver Remote ID field only when the chosen blaster is Tuya.
  const selectedItach = itachs.find((i) => i.id === watch('itach_device_id'));
  const itachIsTuya = selectedItach?.driver_type === 'tuya_ir_blaster';

  const onSubmit = async (data) => {
    // Preserve any existing config keys (e.g. broadlink_ip); only touch the
    // per-receiver Tuya remote.
    const config = { ...(editing?.config ?? {}) };
    if (itachIsTuya && data.tuya_remote_id?.trim()) config.tuya_remote_id = data.tuya_remote_id.trim();
    else delete config.tuya_remote_id;

    const payload = {
      name: data.name,
      type: data.type,
      connected_player_id:  data.connected_player_id  || null,
      connected_encoder_id: data.connected_encoder_id || null,
      itach_device_id:      data.itach_device_id      || null,
      config:               Object.keys(config).length ? config : null,
    };
    try {
      if (editing) {
        await adminApi.put(`/companies/${companyId}/receivers/${editing.id}/update`, payload);
        showToast('success', 'Saved', `${data.name} updated.`);
      } else {
        await adminApi.post(`/companies/${companyId}/receivers/create`, payload);
        showToast('success', 'Created', `${data.name} added.`);
      }
      onSaved();
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed to save.');
    }
  };

  return (
    <Modal open={open} onClose={onClose}
      title={editing ? '✎ Edit Receiver' : '📡 Add Receiver'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {editing ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Field icon="🏷️" label="Name" required error={errors.name?.message}>
          <Input placeholder="e.g. beIN Sports Receiver" {...register('name', { required: 'Required' })} />
        </Field>
        <Field icon="📋" label="Type" required>
          <Select {...register('type', { required: true })}>
            {RECEIVER_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </Field>
        <Field icon="📱" label="Connected Player"
               hint="The Android device that displays the HDMI-IN campaign.">
          <Select {...register('connected_player_id')}>
            <option value="">Select Android Player…</option>
            {players.filter((p) => !takenPlayers.has(p.id)).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name ? `${p.name} (${p.serial_name})` : p.serial_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field icon="🔌" label="Connected Encoder"
               hint="The PTN encoder that captures this receiver's HDMI signal.">
          <Select {...register('connected_encoder_id')}>
            <option value="">Select PTN Encoder…</option>
            {encoders.filter((e) => !takenEncoders.has(e.id)).map((e) => (
              <option key={e.id} value={e.id}>
                {e.port_number ? `${e.name} (port ${e.port_number})` : e.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field icon="📡" label="Connected iTach"
               hint="Optional — iTach IR blaster used to tune this receiver's channels.">
          <Select {...register('itach_device_id')}>
            <option value="">None</option>
            {itachs.map((i) => (
              <option key={i.id} value={i.id}>
                {i.ip_address ? `${i.name} (${i.ip_address})` : i.name}
              </option>
            ))}
          </Select>
        </Field>

        {itachIsTuya && (
          <Field icon="🎛️" label="Tuya Remote ID" required error={errors.tuya_remote_id?.message}
                 hint="Which learned Tuya remote tunes THIS receiver — the STB/TV/etc. brand on the shared blaster. From the Tuya remotes list (differs per receiver).">
            <Input placeholder="e.g. bf6a276152b5717581qoqx"
                   {...register('tuya_remote_id', { required: itachIsTuya ? 'Required' : false })} />
          </Field>
        )}
      </form>
    </Modal>
  );
}

function ChannelsModal({ companyId, receiver, onClose }) {
  const { showToast } = useGlobal();
  const { rows, loading, refresh } = useAdminList(`/companies/${companyId}/receivers/${receiver.id}/channels`);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (open) reset({
      name: edit?.name ?? '', channel_number: edit?.channel_number ?? '',
      logo_url: edit?.logo_url ?? '', ir_command: edit?.ir_command ?? '',
    });
  }, [open, edit, reset]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name, channel_number: data.channel_number,
      logo_url: data.logo_url || null, ir_command: data.ir_command || null,
    };
    try {
      if (edit) {
        await adminApi.put(`/companies/${companyId}/channels/${edit.id}/update`, payload);
        showToast('success', 'Saved', `${data.name} updated.`);
      } else {
        await adminApi.post(`/companies/${companyId}/receivers/${receiver.id}/channels`, payload);
        showToast('success', 'Created', `${data.name} added.`);
      }
      setOpen(false); refresh();
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.');
    }
  };

  const onDelete = async (ch) => {
    try {
      await adminApi.delete(`/companies/${companyId}/channels/${ch.id}/delete`);
      showToast('success', 'Deleted', `${ch.name} removed.`);
      refresh();
    } catch (e) {
      showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.');
    }
  };

  return (
    <Modal open={!!receiver} onClose={onClose}
      title={`📺 Channels — ${receiver.name}`} size="lg">
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <p className="text-sm text-text-muted">{rows.length} channels</p>
          <button className="btn-primary text-sm" onClick={() => { setEdit(null); setOpen(true); }}>+ Add Channel</button>
        </div>
        {loading ? (
          <div className="p-6 flex justify-center"><Spinner /></div>
        ) : rows.length === 0 ? (
          <Empty icon="📺" title="No channels yet"
            description="Add channels so the Remote Control can switch this receiver."
            ctaLabel="+ Add Channel" onCta={() => { setEdit(null); setOpen(true); }} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/[0.08]">
                <th className="table-header">Logo</th>
                <th className="table-header">Name</th>
                <th className="table-header">Channel #</th>
                <th className="table-header">IR Command</th>
                <th className="table-header w-24">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((ch) => (
                  <tr key={ch.id} className="border-b border-white/[0.04]">
                    <td className="table-cell">{ch.logo_url
                      ? <img src={ch.logo_url} alt="" className="w-6 h-6 object-contain" onError={(e) => e.currentTarget.style.display = 'none'} />
                      : <span className="text-text-muted">—</span>}</td>
                    <td className="table-cell">{ch.name}</td>
                    <td className="table-cell">{ch.channel_number ?? '—'}</td>
                    <td className="table-cell"><code className="text-xs truncate max-w-[200px] block">{ch.ir_command ?? '—'}</code></td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button className="btn-icon" onClick={() => { setEdit(ch); setOpen(true); }}>✎</button>
                        <button className="btn-icon text-danger" onClick={() => onDelete(ch)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)}
        title={edit ? '✎ Edit Channel' : '📺 Add Channel'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
              {edit ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <Field icon="🏷️" label="Name" required error={errors.name?.message}>
            <Input placeholder="e.g. beIN Sports 1" {...register('name', { required: 'Required' })} />
          </Field>
          <Field icon="🔢" label="Channel Number">
            <Input placeholder="e.g. 4" {...register('channel_number')} />
          </Field>
          <Field icon="🖼️" label="Logo URL">
            <Input placeholder="https://…" {...register('logo_url')} />
          </Field>
          <Field icon="📡" label="IR Command" hint="Pronto/iTach formatted; used to tune this channel.">
            <Textarea rows={3} placeholder="sendir,…" {...register('ir_command')} />
          </Field>
        </form>
      </Modal>
    </Modal>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Section: SCREENS
 * ──────────────────────────────────────────────────────────────────── */
function ScreensSection({ companyId }) {
  const { showToast } = useGlobal();
  const { rows, loading, refresh } = useAdminList(`/companies/${companyId}/screens`);
  const { rows: decoders } = useAdminList(`/companies/${companyId}/hardware?role=decoder`);

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del,  setDel]  = useState(null);

  const takenDecoders = new Set(rows.filter((r) => r.id !== edit?.id && r.decoder_id).map((r) => r.decoder_id));

  const onDelete = async () => {
    if (!del) return;
    try {
      await adminApi.delete(`/companies/${companyId}/screens/${del.id}/delete`);
      showToast('success', 'Deleted', `${del.name} removed.`);
      setDel(null); refresh();
    } catch (e) { showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.'); }
  };

  const cols = [
    { key: 'name', header: 'Name', render: (r) => <strong>🖼️ {r.name}</strong> },
    { key: 'location', header: 'Location',
      render: (r) => r.location ?? <span className="text-text-muted">—</span> },
    { key: 'decoder', header: 'Decoder',
      render: (r) => r.decoder?.name
        ? <span>{r.decoder.name}{r.decoder.port_number ? <span className="text-text-muted"> — port {r.decoder.port_number}</span> : null}</span>
        : <span className="text-text-muted">—</span> },
    { key: 'has_audio_out', header: 'Audio',
      render: (r) => r.has_audio_out ? <Badge variant="teal" dot>Audio</Badge> : <span className="text-text-muted">—</span> },
  ];

  return (
    <Section title="Screens" count={rows.length} addLabel="Add Screen" onAdd={() => { setEdit(null); setOpen(true); }}>
      {loading ? (
        <div className="p-6 flex justify-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <Empty icon="🖼️" title="No screens yet"
          description="Bind a venue display to a PTN decoder port so it can receive routed sources."
          ctaLabel="+ Add Screen" onCta={() => { setEdit(null); setOpen(true); }} />
      ) : (
        <DataTable
          columns={cols} data={rows} total={rows.length} page={0} perPage={rows.length}
          onPageChange={() => {}} onPerPageChange={() => {}}
          actions={(row) => (
            <div className="flex gap-1">
              <button className="btn-icon" onClick={() => { setEdit(row); setOpen(true); }}>✎</button>
              <button className="btn-icon text-danger" onClick={() => setDel(row)}>🗑</button>
            </div>
          )}
        />
      )}
      <ScreenFormModal
        open={open} onClose={() => setOpen(false)}
        companyId={companyId} editing={edit}
        decoders={decoders} takenDecoders={takenDecoders}
        onSaved={() => { setOpen(false); refresh(); }}
      />
      <ConfirmModal
        open={!!del} onClose={() => setDel(null)} onConfirm={onDelete}
        title="Delete screen" message={`Permanently remove "${del?.name}"?`}
      />
    </Section>
  );
}

function ScreenFormModal({ open, onClose, companyId, editing, decoders, takenDecoders, onSaved }) {
  const { showToast } = useGlobal();
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (!open) return;
    reset({
      name: editing?.name ?? '', location: editing?.location ?? '',
      decoder_id: editing?.decoder_id ?? '',
      has_audio_out: !!editing?.has_audio_out,
    });
  }, [open, editing, reset]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name, location: data.location || null,
      decoder_id: data.decoder_id || null,
      has_audio_out: !!data.has_audio_out,
    };
    try {
      if (editing) {
        await adminApi.put(`/companies/${companyId}/screens/${editing.id}/update`, payload);
        showToast('success', 'Saved', `${data.name} updated.`);
      } else {
        await adminApi.post(`/companies/${companyId}/screens/create`, payload);
        showToast('success', 'Created', `${data.name} added.`);
      }
      onSaved();
    } catch (e) { showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.'); }
  };

  return (
    <Modal open={open} onClose={onClose}
      title={editing ? '✎ Edit Screen' : '🖼️ Add Screen'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
            {editing ? 'Save' : 'Create'}
          </button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <Field icon="🏷️" label="Name" required error={errors.name?.message}>
          <Input placeholder="e.g. Main Hall TV 1" {...register('name', { required: 'Required' })} />
        </Field>
        <Field icon="📍" label="Location">
          <Input placeholder="e.g. Main Hall" {...register('location')} />
        </Field>
        <Field icon="🔌" label="Decoder" hint="The PTN decoder port wired to this screen.">
          <Select {...register('decoder_id')}>
            <option value="">Select decoder…</option>
            {decoders.filter((d) => !takenDecoders.has(d.id)).map((d) => (
              <option key={d.id} value={d.id}>
                {d.port_number ? `${d.name} (port ${d.port_number})` : d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox id="has_audio_out" label="Has audio output" {...register('has_audio_out')} />
      </form>
    </Modal>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Section: SCENES — minimal CRUD (Dashboard owns the full action builder)
 * ──────────────────────────────────────────────────────────────────── */
function ScenesSection({ companyId }) {
  const { showToast } = useGlobal();
  const { rows, loading, refresh } = useAdminList(`/companies/${companyId}/scenes`);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del,  setDel]  = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  useEffect(() => {
    if (open) reset({ name: edit?.name ?? '', is_default: !!edit?.is_default });
  }, [open, edit, reset]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      is_default: !!data.is_default,
      // Admin scene CRUD intentionally keeps the existing scene_actions
      // intact on edit and creates an empty action list on new — the full
      // multi-step action builder lives in the Dashboard's ScenesPage.
      scene_actions: edit?.scene_actions ?? [],
    };
    try {
      if (edit) {
        await adminApi.put(`/companies/${companyId}/scenes/${edit.id}/update`, payload);
        showToast('success', 'Saved', `${data.name} updated.`);
      } else {
        await adminApi.post(`/companies/${companyId}/scenes/create`, payload);
        showToast('success', 'Created', `${data.name} added — use the venue dashboard to add actions.`);
      }
      setOpen(false); refresh();
    } catch (e) { showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.'); }
  };

  const onDelete = async () => {
    if (!del) return;
    try {
      await adminApi.delete(`/companies/${companyId}/scenes/${del.id}/delete`);
      showToast('success', 'Deleted', `${del.name} removed.`);
      setDel(null); refresh();
    } catch (e) { showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.'); }
  };

  const activate = async (s) => {
    try {
      await adminApi.post(`/companies/${companyId}/scenes/${s.id}/activate`);
      showToast('success', 'Activated', `${s.name} fired.`);
    } catch (e) { showToast('error', 'Error', e?.response?.data?.message ?? 'Failed.'); }
  };

  const cols = [
    { key: 'name', header: 'Name', render: (r) => <strong>🎬 {r.name}</strong> },
    { key: 'actions_count', header: 'Actions',
      render: (r) => <Badge variant="muted">{r.scene_actions_count ?? r.scene_actions?.length ?? 0}</Badge> },
    { key: 'is_default', header: 'Default',
      render: (r) => r.is_default ? <Badge variant="teal">Default</Badge> : <span className="text-text-muted">—</span> },
  ];

  return (
    <Section title="Scenes" count={rows.length} addLabel="Add Scene" onAdd={() => { setEdit(null); setOpen(true); }}>
      {loading ? (
        <div className="p-6 flex justify-center"><Spinner size="lg" /></div>
      ) : rows.length === 0 ? (
        <Empty icon="🎬" title="No scenes yet"
          description="Scenes bundle multi-step routines (channel switch + matrix route + audio). Add the name here; the venue owner builds out the action list from the dashboard."
          ctaLabel="+ Add Scene" onCta={() => { setEdit(null); setOpen(true); }} />
      ) : (
        <DataTable
          columns={cols} data={rows} total={rows.length} page={0} perPage={rows.length}
          onPageChange={() => {}} onPerPageChange={() => {}}
          actions={(row) => (
            <div className="flex gap-1">
              <button className="btn-icon text-teal" title="Activate" onClick={() => activate(row)}>▶</button>
              <button className="btn-icon" title="Edit" onClick={() => { setEdit(row); setOpen(true); }}>✎</button>
              <button className="btn-icon text-danger" title="Delete" onClick={() => setDel(row)}>🗑</button>
            </div>
          )}
        />
      )}

      <Modal open={open} onClose={() => setOpen(false)}
        title={edit ? '✎ Edit Scene' : '🎬 Add Scene'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
              {edit ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <Field icon="🏷️" label="Name" required error={errors.name?.message}>
            <Input placeholder="e.g. Match Mode" {...register('name', { required: 'Required' })} />
          </Field>
          <Checkbox id="is_default" label="Set as default scene" {...register('is_default')} />
          <p className="text-xs text-text-muted">
            ℹ️ Use the venue dashboard (Hardware → Scenes) to add multi-step actions
            (campaign switch, channel switch, PTN command, audio control).
          </p>
        </form>
      </Modal>
      <ConfirmModal
        open={!!del} onClose={() => setDel(null)} onConfirm={onDelete}
        title="Delete scene" message={`Permanently remove "${del?.name}"?`}
      />
    </Section>
  );
}
